<script lang="ts">
	import type { PipelineProgress } from '#lib/pipeline';
	import { t } from '#lib/i18n/state.svelte';

	interface Props {
		fileName: string;
		progress: PipelineProgress;
	}

	let { fileName, progress }: Props = $props();

	const steps = $derived([
		{ key: 'extract', label: t('proc.file'), done: progress.phase === 'analyze' },
		{ key: 'analyze', label: t('proc.analyze'), done: false },
		{ key: 'report', label: t('proc.report'), done: false }
	]);

	const pct = $derived(Math.round(Math.max(2, Math.min(99, progress.ratio * 100))));
	const stageText = $derived(t(progress.stageKey, progress.stageParams));
</script>

<div class="processing card" role="status" aria-live="polite">
	<div class="spinner" aria-hidden="true"></div>
	<div class="content">
		<p class="file mono">{fileName}</p>
		<h2>{stageText}</h2>
		<div class="bar" aria-hidden="true">
			<div class="fill" style:width="{pct}%"></div>
		</div>
		<div class="row-between meta">
			<span>%{pct}</span>
			{#if progress.page && progress.total}
				<span>{t('proc.pageOf', { page: progress.page, total: progress.total })}</span>
			{:else}
				<span>{progress.phase === 'extract' ? t('proc.local') : t('proc.evaluating')}</span>
			{/if}
		</div>

		<ol class="steps">
			{#each steps as step (step.key)}
				<li class:active={progress.phase === step.key || (step.key === 'extract' && progress.phase === 'extract')}>
					<span class="dot"></span>
					{step.label}
				</li>
			{/each}
		</ol>
	</div>
</div>

<style>
	.processing {
		display: flex;
		gap: 1.5rem;
		align-items: flex-start;
		padding: clamp(1.5rem, 3vw, 2.2rem);
		max-width: 620px;
		margin-inline: auto;
		animation: fade-up 0.4s var(--ease) both;
	}
	.spinner {
		width: 34px;
		height: 34px;
		flex: none;
		border-radius: 50%;
		border: 3px solid var(--line);
		border-top-color: var(--accent);
		animation: spin 0.9s linear infinite;
		margin-top: 4px;
	}
	.content {
		flex: 1;
		min-width: 0;
	}
	.file {
		margin: 0 0 0.35rem;
		color: var(--muted);
		font-size: 0.78rem;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	h2 {
		font-family: var(--font-ui);
		font-size: 1.02rem;
		font-weight: 600;
		margin: 0 0 0.85rem;
	}
	.bar {
		height: 7px;
		border-radius: 99px;
		background: var(--paper-2);
		overflow: hidden;
	}
	.fill {
		height: 100%;
		border-radius: inherit;
		background: linear-gradient(90deg, var(--accent), #2dd4bf);
		transition: width 0.35s var(--ease);
	}
	.meta {
		margin-top: 0.45rem;
		font-size: 0.76rem;
		color: var(--muted);
		font-variant-numeric: tabular-nums;
	}
	.steps {
		display: flex;
		gap: 1rem;
		list-style: none;
		padding: 0;
		margin: 1.2rem 0 0;
		font-size: 0.78rem;
		color: var(--muted);
		flex-wrap: wrap;
	}
	.steps li {
		display: flex;
		align-items: center;
		gap: 0.35rem;
	}
	.dot {
		width: 6px;
		height: 6px;
		border-radius: 50%;
		background: var(--line-strong);
	}
	.steps li.active {
		color: var(--ink);
		font-weight: 600;
	}
	.steps li.active .dot {
		background: var(--accent);
		animation: pulse-ring 1.8s infinite;
	}
</style>
